import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const accountCache = new CacheManager('VueCache', 'cache', {
	maxAge: 7 * 24 * 60 * 60 * 1000,
	logCategory: 'Account',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.accounts.`,
});

export async function getAccounts(forceRefresh = false) {
	try {
		if (!forceRefresh) {
			const cached = await accountCache.get('all');
			if (cached) {
				log('info', 'Account', 'Returning cached accounts');
				return cached;
			}

			try {
				const legacyData = await iframeDataBridge.getStorageAsync('ACCOUNTS');
				if (legacyData) {
					log('info', 'Account', '📦 Seeding Vue cache from legacy storage (ACCOUNTS)');
					await accountCache.put('all', legacyData);
					return legacyData;
				}
			} catch (legacyError) {
				log('warn', 'Account', `⚠️ Legacy storage check failed: ${legacyError.message}`);
			}
		}

		if (iframeDataBridge.isOffline() && !forceRefresh) {
			log('warn', 'Account', 'Offline: No cached accounts available');
			return [];
		}

		const apiUrl = getApiUrl('Distributor/Accounts');
		const response = await authenticatedFetch(apiUrl, { method: 'GET' }, 'tokenSales');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		await accountCache.put('all', data);

		log('info', 'Account', `Fetched ${data?.length || 0} accounts`);
		return data;
	} catch (error) {
		log('error', 'Account', `Error loading accounts: ${error.message}`);

		const cached = await accountCache.get('all');
		if (cached) {
			log('info', 'Account', 'Returning cached accounts after error');
			return cached;
		}

		throw error;
	}
}

export async function getAccountById(id) {
	try {
		const cached = await accountCache.get(id);
		if (cached) {
			return cached;
		}

		if (iframeDataBridge.isOffline()) {
			log('warn', 'Account', `Offline: Account ${id} not found in cache`);
			return null;
		}

		const apiUrl = getApiUrl(`Distributor/Accounts/${id}`);
		const response = await authenticatedFetch(apiUrl, {}, 'tokenSales');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		await accountCache.put(id, data);

		return data;
	} catch (error) {
		log('error', 'Account', `Error loading account ${id}: ${error.message}`);
		throw error;
	}
}

export async function clearAccountCache(id = null) {
	if (id) {
		await accountCache.delete(id);
	} else {
		await accountCache.clearAll();
	}
}

export async function cleanOldCacheEntries(maxAgeInDays = 7) {
	await accountCache.cleanOld(maxAgeInDays);
}

export async function getCacheStats() {
	return await accountCache.getStats();
}

cleanOldCacheEntries();
